import {
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useState,
} from "react";

import { useIsMobile } from "../hooks/useIsMobile";
import { cn } from "../utils";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./Tooltip";

interface IProps {
  className?: string;
  contentClassName?: string;
}

/**
 * `null` when the element cannot be measured — it is detached or hidden, and so reports 0×0.
 *
 * That is NOT evidence the text fits, and treating it as such is an infinite loop: flipping to the
 * clipped state re-parents the div into a trigger, which leaves the previous node detached for one
 * render. Measuring it then returns 0×0, flips the state back, and the cycle repeats.
 */
const isOverflowing = (el: HTMLDivElement): boolean | null => {
  if (el.offsetWidth === 0 && el.offsetHeight === 0) {
    return null;
  }

  return el.offsetHeight < el.scrollHeight || el.offsetWidth < el.scrollWidth;
};

/**
 * Clips its text to one line with an ellipsis, and reveals the full value on hover (desktop) or
 * click/tap (mobile) — but only when it is actually clipped, so text that fits carries no
 * affordance and no extra tab stop.
 *
 * Whether text fits depends on two things, and both are watched. The element's WIDTH: in a fluid
 * table column the same cell overflows at one viewport and fits at another, which the
 * `ResizeObserver` catches. And the CONTENT: a refetch can swap a longer email into a box that
 * never changes size, which a ResizeObserver does not report — so the measurement also re-runs on
 * every render, which is two cheap layout reads.
 *
 * Desktop and mobile are chosen in JS rather than rendered both and hidden with CSS. Two rendered
 * copies would mean two elements holding the ref — and the hidden one measures 0×0, which reads as
 * "fits" and would fight the visible one's measurement.
 */
export const TruncatedText = ({
  children,
  className = "",
  contentClassName = "",
}: PropsWithChildren<IProps>) => {
  const [isOverflown, setIsOverflown] = useState(false);
  // The measured node is held in state, not a ref, so that swapping it re-runs the observer effect
  // against the new element.
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const isMobile = useIsMobile();

  // Deliberately no dependency array: the content can change without the box resizing, and this is
  // the only thing that notices. Unmeasurable nodes are skipped rather than recorded, and
  // `setIsOverflown` bails out when the value is unchanged, so a settled cell stops re-rendering.
  useEffect(() => {
    if (!node) {
      return;
    }

    const measured = isOverflowing(node);

    if (measured !== null) {
      setIsOverflown(measured);
    }
  });

  useEffect(() => {
    if (!node) {
      return;
    }

    const observer = new ResizeObserver(() => {
      const measured = isOverflowing(node);

      if (measured !== null) {
        setIsOverflown(measured);
      }
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, [node]);

  const text: ReactNode = (
    <div ref={setNode} className={cn("truncate", className)}>
      {children}
    </div>
  );

  if (!isOverflown) {
    return text;
  }

  // Once clipped, the trigger is the ONLY route to the full value, so it has to be reachable by
  // keyboard — `asChild` on a bare `div` gives Radix's handlers but no focusability. The measured
  // div is wrapped rather than swapped for the button so that what is measured never changes:
  // making the element itself a button risks its own box altering the overflow verdict.
  const trigger = (
    <button type="button" className="block w-full cursor-default text-left">
      {text}
    </button>
  );

  if (isMobile) {
    return (
      <Popover>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent className={cn("w-fit", contentClassName)}>
          <p>{children}</p>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent>
          <p className={contentClassName}>{children}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
